import React, { createContext, useCallback, useMemo } from 'react';
import { fetchAuthSession, getCurrentUser, signOut as awsSignOut } from 'aws-amplify/auth';
import moment from 'moment';
import { useQuery } from '@tanstack/react-query'; // Import useQuery
import { getDataAPI, putDataAPI } from '../../Helpers/FetchAPI';
import { logger } from '../../utils/logger.js';

export const AuthContext = createContext({
  userData: null,
  error: null,
  isLoading: false,
  isSuccess: false,
  signOut: () => {},
  getUser: () => {},
  refetch: () => {}
});

const AuthContextProvider = ({ children }) => {
  const getUser = useCallback(async (email) => {
    const res = await getDataAPI(`/user/get-user-information/${email}`);
    return res.data;
  }, []);

  const getAuthenticatedUser = useCallback(async () => {
    try {
      const user = await getCurrentUser();
      const session = await fetchAuthSession();
      let loginId = user.signInDetails?.loginId ?? session.tokens.idToken.payload.email
      const lastLogin = moment.unix(session.tokens.accessToken.payload.auth_time);
      const userData = await getUser(loginId);

      if (userData.lastLogin !== lastLogin) {
        await putDataAPI(`/user/logs-user/${loginId}`, {
          lastLogin,
          awsUserId: user.username
        });
      }

      return userData;
    } catch (error) {
      logger.error('Error getting authenticated user', error);
      throw error;
    }
  }, [getUser]);

  const { data: userData, error, isLoading, refetch, isSuccess } = useQuery({
    queryKey: ['authenticatedUser'],
    queryFn: getAuthenticatedUser
  });

  const signOut = useCallback(async (global = false) => {
    await awsSignOut({ global });
    window.location.reload('/');
  }, []);

  const value = useMemo(
    () => ({
      userData,
      error,
      isLoading,
      signOut,
      getUser,
      refetch,
      isSuccess
    }),
    [userData, error, isLoading, signOut, getUser, refetch, isSuccess]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContextProvider;
